/**
 * A sign-in that comes back with `?error=<code>`, as pure rules: where a
 * failed sign-in returns to, which codes Babia explains, and what each one
 * tells the parent. better-auth's OAuth callback sends the codes (the
 * button's `errorCallbackURL`, else `/` in production), and so does the OAuth
 * provider when an assistant's authorization is refused before it can return
 * to the assistant. Never shows `error_description`: anyone can put words in
 * a link.
 */
import { alertReturnPath } from "./push-state.ts";

/** The providers a parent can sign in with (server/services/sign-in-rules.ts). */
export type SignInProvider = "google" | "facebook";

/** The codes Babia explains, and the only values analytics records. */
const REASONS = [
  // Same email as an account that signs in another way; never linked.
  "account_not_linked",
  // Facebook gave no email address (declined, or none on the account).
  "email_not_found",
  // The parent cancelled at Google or Facebook.
  "access_denied",
  // The sign-in's cookie was lost on the way back.
  "state_mismatch",
  // An assistant's registration Babia can't use.
  "invalid_client",
  "invalid_redirect",
  "client_disabled",
  "unauthorized_client",
  "unsupported_response_type",
  // An assistant's signed sign-in link expired (10 minutes).
  "invalid_signature",
] as const;

export type SignInReason = (typeof REASONS)[number] | "other";

/** A returned code as a known reason, or "other". */
export function signInReason(code: string | null | undefined): SignInReason {
  return REASONS.find((r) => r === code) ?? "other";
}

/** The sentence a parent sees for a failed sign-in. */
export function signInErrorMessage(reason: SignInReason): string {
  switch (reason) {
    case "account_not_linked":
      return "This email already has a Babia account. Sign in the way you did the first time.";
    case "email_not_found":
      return "Facebook didn’t share an email address, and Babia needs one. Tap Continue with Facebook and allow your email, or use Google.";
    case "access_denied":
      return "Sign-in was cancelled.";
    case "invalid_client":
    case "invalid_redirect":
    case "client_disabled":
    case "unauthorized_client":
    case "unsupported_response_type":
      return "Your assistant’s connection to Babia is out of date. Remove Babia from your assistant and add it again.";
    case "invalid_signature":
      return "This sign-in link expired. Go back to your assistant and start again.";
    default:
      return "Sign-in didn’t finish. Please try again.";
  }
}

/**
 * The sentence for a refused password sign-in (components/sign-in.tsx), from
 * the response's HTTP status and its `error` code. Only the reviewer accounts
 * have a password (server/auth.ts), so a parent who tries one learns to use
 * Google or Facebook. A wrong password and an email without a password get the
 * same answer, from the server and here.
 */
export function passwordSignInMessage(status: number | undefined, code: string | null): string {
  const reason = signInReason(code);
  if (reason !== "other") return signInErrorMessage(reason);
  if (status === 401) {
    return "That email and password don’t match an account that Babia set up. Parents sign in with Google or Facebook.";
  }
  if (status === 429) return "Too many tries. Wait a minute, then try again.";
  return "Sign-in didn’t finish. Please try again.";
}

/** The query parameters a failed sign-in adds. */
const RETURNED = ["error", "error_description"];

/** The code a sign-in came back with, or null. */
export function returnedSignInError(search: string): string | null {
  return new URLSearchParams(search).get("error");
}

/**
 * The address without the error a sign-in came back with, or null when it
 * has none. Other parameters keep their values, because an assistant's query
 * is signed (the server checks the decoded values, so re-encoding is safe).
 */
export function cleanedReturnAddress(location: {
  pathname: string;
  search: string;
  hash: string;
}): string | null {
  const params = new URLSearchParams(location.search);
  if (!RETURNED.some((key) => params.has(key))) return null;
  for (const key of RETURNED) params.delete(key);
  const search = params.toString();
  return location.pathname + (search ? `?${search}` : "") + location.hash;
}

/**
 * Where a failed sign-in comes back to. An assistant's sign-in returns to its
 * `/login` page with the signed query, so the parent can try again (the
 * signature covers only the parameters it names, so a returned error does not
 * break it). Anything else returns where a successful one would.
 */
export function errorReturnPath(
  location: { pathname: string; search: string },
  assistant: boolean,
): string {
  if (!assistant) return alertReturnPath(location);
  const params = new URLSearchParams(location.search);
  for (const key of RETURNED) params.delete(key);
  const search = params.toString();
  return search ? `/login?${search}` : "/login";
}
